/* import __COLOCATED_TEMPLATE__ from './spam.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import { SpamResource } from 'embercom/components/inbox2/spam-resource';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import type Inbox2Counters from 'embercom/services/inbox2-counters';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type SpamMessage } from 'embercom/objects/inbox/spam';
import { taskFor } from 'ember-concurrency-ts';
import Spam from 'embercom/objects/inbox/inboxes/spam';
import { debounce } from '@ember/runloop';
import moment from 'moment-timezone';

const SPAM_HISTORY_LIMIT_DAYS = 60;

const ALL_COLUMNS = [
  {
    label: 'name',
    valuePath: 'name',
    width: '220px',
  },
  {
    label: 'email',
    valuePath: 'email',
    width: '270px',
  },
  {
    label: 'subject',
    valuePath: 'emailSubject',
    width: '350px',
  },
  {
    label: 'date',
    valuePath: 'createdAt',
    width: '150px',
  },
  {
    label: 'rejection_reason',
    valuePath: 'rejectionReasonLabel',
    width: '150px',
  },
];

interface Args {}

interface Signature {
  Args: Args;
}

export default class SpamComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare inbox2Counters: Inbox2Counters;

  @tracked messageToPreview: SpamMessage | undefined;
  @tracked processingMessage: SpamMessage | undefined;
  @tracked emailFilter = '';
  @tracked startDate: Date | null = null;
  @tracked endDate: Date | null = null;

  spamInbox = new Spam(0); // The count does not matter here as we are just using it to generate a key for the Inbox counters service

  columns = ALL_COLUMNS;

  readonly spamResource = SpamResource.from(this);

  get startDateAsMoment() {
    return this.startDate ? moment(this.startDate) : null;
  }

  get endDateAsMoment() {
    return this.endDate ? moment(this.endDate) : null;
  }

  get startDateForAPI() {
    if (!this.startDate) {
      return undefined;
    }
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(this.startDate).tz(userTimezone).startOf('day').format();
  }

  get endDateForAPI() {
    if (!this.endDate) {
      return undefined;
    }
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(this.endDate).tz(userTimezone).endOf('day').format();
  }

  get spamHistoryLimitDate() {
    return moment().subtract(SPAM_HISTORY_LIMIT_DAYS, 'days').toDate();
  }

  get todaysDate() {
    return moment().toDate();
  }

  @action openPreviewPanel(spamMessage: SpamMessage) {
    this.messageToPreview = spamMessage;
  }

  @action hidePreviewPanel() {
    this.messageToPreview = undefined;
  }

  @action async markAsNotSpam(spamMessage: SpamMessage, event: Event) {
    event.stopPropagation();
    if (spamMessage?.id) {
      this.processingMessage = spamMessage;
      let messageProcessed = await taskFor(this.spamResource.markAsNotSpam).perform(spamMessage.id);
      if (messageProcessed) {
        this.spamResource.spamMessages.removeObject(spamMessage);
        this.messageToPreview = undefined;
        this.inbox2Counters.decrementCount(this.spamInbox);
      }
      this.processingMessage = undefined;
    }
  }

  @action updateEmailFilter(event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;

    debounce(this, 'applyEmailFilter', value, 300);
  }

  @action applyEmailFilter(value: string) {
    this.emailFilter = value;
    this.applyFilters();
  }

  @action clearEmailFilter() {
    this.emailFilter = '';
    this.applyFilters();
  }

  @action setStartDate(datePickerResult: any) {
    if (datePickerResult && datePickerResult.moment) {
      this.startDate = datePickerResult.moment.toDate();

      // If no end date is set, automatically set end date to today
      if (!this.endDate) {
        this.endDate = this.todaysDate;
      }
    } else {
      this.startDate = null;
    }
    this.applyFilters();
  }

  @action setEndDate(datePickerResult: any) {
    if (datePickerResult && datePickerResult.moment) {
      this.endDate = datePickerResult.moment.toDate();

      // If no start date is set, automatically set start date to spam history limit
      if (!this.startDate) {
        this.startDate = moment().subtract(SPAM_HISTORY_LIMIT_DAYS, 'days').toDate();
      }
    } else {
      this.endDate = null;
    }
    this.applyFilters();
  }

  @action clearStartDate() {
    this.startDate = null;
    this.applyFilters();
  }

  @action clearEndDate() {
    this.endDate = null;
    this.applyFilters();
  }

  @action clearDateFilters() {
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  @action clearAllFilters() {
    this.emailFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  private applyFilters() {
    this.spamResource.applyFilters({
      from_email: this.emailFilter,
      start_date: this.startDateForAPI,
      end_date: this.endDateForAPI,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Spam': typeof SpamComponent;
    'inbox2/spam': typeof SpamComponent;
  }
}
